import React, { useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import useAuth from '../Hooks/use-auth';
import { useCreatorAuth } from '../Hooks/creator-use-auth';
import { lightTheme } from '../Utils/baseTheme';

// Import the navbar components for each user type
import Navbar from './Navbar/NavBar'; // Regular user navbar
import CreatorNavbar from './CreatorNavbar';
import BlitzHeader from './BlitzHeader';
import useIsDesktop from '../Hooks/useIsDesktop';
import { drawerWidth } from '../Utils/constants';

// Layout mixins (copied from BlitzLayout)
const openedMixin = (theme) => ({
  minWidth: {
    xs: '100%',
    md: `calc(100% - ${drawerWidth})`,
  },
  marginInlineStart: {
    xs: 0,
    md: drawerWidth,
  },
  transition: theme.transitions.create(['min-width', 'margin-inline-start'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create(['min-width', 'margin-inline-start'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  minWidth: {
    xs: '100%',
    md: `calc(100% - ${lightTheme.spacing(8)} + 1px)`,
  },
  marginInlineStart: {
    xs: 0,
    md: `calc(${lightTheme.spacing(8)} - 1px)`,
  },
});

const GlobalLayout = ({ onHelp, children }) => {
  const { isAuthorized: isUserAuthenticated } = useAuth();
  const { isAuthorized: isCreatorAuthenticated, isInitialized } = useCreatorAuth();
  const isDesktop = useIsDesktop();
  const [isOpen, setIsOpen] = useState(isDesktop);
  const [mobileActions, setMobileActions] = useState([]);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const isExtended = (!isDesktop || isOpen);

  // Wait for creator auth to initialize
  if (!isInitialized) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  const handleGetMobileActions = (actions) => setMobileActions(actions);

  // Render the appropriate navbar based on authentication
  const renderNavbar = () => {
    if (isCreatorAuthenticated) {
      // Use creator navbar - you might need to adjust the props based on your CreatorNavbar component
      return (
        <CreatorNavbar
          isDesktop={isDesktop}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          onClickHelp={onHelp}
          onOpen={() => setIsOpen(true)}
          onGetMobileActions={handleGetMobileActions}
          notificationAnchor={notificationAnchor}
          onSetNotificationAnchor={setNotificationAnchor}
        />
      );
    } else if (isUserAuthenticated) {
      // Use regular user navbar
      return (
        <Navbar
          isDesktop={isDesktop}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          onClickHelp={onHelp}
          onOpen={() => setIsOpen(true)}
          onGetMobileActions={handleGetMobileActions}
          notificationAnchor={notificationAnchor}
          onSetNotificationAnchor={setNotificationAnchor}
        />
      );
    }
    return null;
  };

  // If neither user type is authenticated, just render children without layout
  if (!isUserAuthenticated && !isCreatorAuthenticated) {
    return <>{children}</>;
  }

  return (
    <>
      <ThemeProvider theme={lightTheme}>
        {renderNavbar()}
        
        {/* Show header ONLY on mobile */}
        {!isDesktop && (
          <>
            <BlitzHeader
              menuButton={null} // You can implement menu button logic if needed
              isDesktop={isDesktop}
              mobileActions={mobileActions}
            />
            {/* <Toolbar /> */}
          </>
        )}
      </ThemeProvider>
      
      <Box
        id='GlobalPageContainer'
        sx={(theme) => ({
          ...(isExtended ? openedMixin(theme) : closedMixin(theme)),
        })}
      >
        {children}
      </Box>
    </>
  );
};

export default GlobalLayout;